import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Slide,
  Fade,
  Zoom,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AdminCategory,
  createAdminCategory,
  deleteAdminCategory,
  getAdminCategories,
  updateAdminCategory,
} from "../api/adminCategoriesApi";

type FormState = {
  name: string;
  slug: string;
};

const initialForm: FormState = {
  name: "",
  slug: "",
};

function slugify(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function CategoriesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState<FormState>(initialForm);
  const [selected, setSelected] = useState<AdminCategory | null>(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState("");

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminCategories();
      setCategories(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return categories;

    return categories.filter((category) => {
      return (
        category.name.toLowerCase().includes(q) ||
        category.slug.toLowerCase().includes(q)
      );
    });
  }, [categories, search]);

  const resetForm = () => {
    setForm(initialForm);
    setSelected(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateOpen(true);
  };

  const openEditDialog = (category: AdminCategory) => {
    setSelected(category);
    setForm({
      name: category.name,
      slug: category.slug,
    });
    setEditOpen(true);
  };

  const openDeleteDialog = (category: AdminCategory) => {
    setSelected(category);
    setDeleteOpen(true);
  };

  const handleCloseCreate = () => {
    if (!saving) {
      setCreateOpen(false);
      resetForm();
    }
  };

  const handleCloseEdit = () => {
    if (!saving) {
      setEditOpen(false);
      resetForm();
    }
  };

  const handleCloseDelete = () => {
    if (!deleting) {
      setDeleteOpen(false);
      resetForm();
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError("Category name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await createAdminCategory({
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
      });

      setCreateOpen(false);
      resetForm();
      setSnackbar("Category created successfully.");
      await loadCategories();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selected) return;

    if (!form.name.trim()) {
      setError("Category name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await updateAdminCategory(selected.id, {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
      });

      setEditOpen(false);
      resetForm();
      setSnackbar("Category updated successfully.");
      await loadCategories();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to update category.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;

    try {
      setDeleting(true);
      setError("");

      await deleteAdminCategory(selected.id);

      setDeleteOpen(false);
      resetForm();
      setSnackbar("Category deleted successfully.");
      await loadCategories();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to delete category.");
    } finally {
      setDeleting(false);
    }
  };

  const StatCard = ({ value, label, icon: Icon, color }: any) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(color, 0.02)} 100%)`,
        transition: 'transform 0.2s',
        minWidth: 120,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.15)}`,
        }
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.1),
            color: color,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const CategoryCard = ({ category }: { category: AdminCategory }) => {
    const inUse = Number(category.product_count || 0) > 0;

    return (
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 2,
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: alpha(theme.palette.primary.main, 0.3),
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
            transform: 'translateY(-2px)',
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                  {category.name}
                </Typography>
                <Chip
                  label={category.slug}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                />
              </Box>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={() => openEditDialog(category)}
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <Tooltip
                  title={inUse ? "Cannot delete category with products" : "Delete category"}
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={inUse}
                      onClick={() => openDeleteDialog(category)}
                      sx={{ 
                        bgcolor: inUse ? 'transparent' : alpha(theme.palette.error.main, 0.08),
                        '&:hover:not(:disabled)': { bgcolor: alpha(theme.palette.error.main, 0.15) }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pt: 1,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <InventoryIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                <strong>{category.product_count}</strong> products
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const stats = useMemo(() => {
    const total = categories.length;
    const withProducts = categories.filter(c => Number(c.product_count) > 0).length;
    const empty = total - withProducts;
    return { total, withProducts, empty };
  }, [categories]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Stack spacing={2}>
        {/* Header Section - More Compact */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: theme.palette.background.paper,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <CategoryIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Categories
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Manage product categories
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                placeholder="Search categories..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <Button
                variant="contained"
                onClick={openCreateDialog}
                sx={{
                  minWidth: { xs: '100%', sm: 140 },
                  borderRadius: 2,
                  height: 40,
                }}
              >
                Add Category
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Stats Cards - Quick Overview */}
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            overflowX: 'auto', 
            pb: 0.5,
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: 2,
            },
          }}
        >
          <StatCard value={stats.total} label="Total Categories" icon={CategoryIcon} color={theme.palette.primary.main} />
          <StatCard value={stats.withProducts} label="With Products" icon={InventoryIcon} color={theme.palette.success.main} />
          <StatCard value={stats.empty} label="Empty" icon={InventoryIcon} color={theme.palette.warning.main} />
        </Stack>

        {error ? (
          <Slide direction="down" in={!!error}>
            <Alert 
              severity="error" 
              onClose={() => setError("")}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          </Slide>
        ) : null}

        {/* Content Area */}
        <Fade in={!loading}>
          <Box>
            {loading ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 200,
                }}
              >
                <CircularProgress size={32} />
              </Paper>
            ) : filteredCategories.length === 0 ? (
              <Zoom in={true}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <CategoryIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.5) }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    No categories found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {search ? "Try adjusting your search" : "Get started by creating your first category"}
                  </Typography>
                  {!search && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openCreateDialog}
                      size="small"
                    >
                      Create Category
                    </Button>
                  )}
                </Paper>
              </Zoom>
            ) : (
              <>
                {/* Desktop/Tablet Table View */}
                {!isMobile && (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Slug</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Products</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCategories.map((category) => {
                          const inUse = Number(category.product_count || 0) > 0;

                          return (
                            <TableRow 
                              key={category.id} 
                              hover
                              sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                transition: 'background-color 0.2s',
                              }}
                            >
                              <TableCell>
                                <Typography fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                  {category.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={category.slug}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    height: 24,
                                    '& .MuiChip-label': { 
                                      px: 1.5, 
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {category.product_count}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Edit category" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditDialog(category)}
                                      sx={{ 
                                        color: theme.palette.text.secondary,
                                        '&:hover': { 
                                          color: theme.palette.primary.main,
                                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                                        }
                                      }}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip
                                    title={inUse ? "Cannot delete - has products" : "Delete category"}
                                    arrow
                                    placement="top"
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={inUse}
                                        onClick={() => openDeleteDialog(category)}
                                        sx={{ 
                                          opacity: inUse ? 0.5 : 1,
                                          '&:hover:not(:disabled)': { 
                                            bgcolor: alpha(theme.palette.error.main, 0.08)
                                          }
                                        }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                )}

                {/* Mobile Card Grid View */}
                {isMobile && (
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    {filteredCategories.map((category) => (
                      <CategoryCard key={category.id} category={category} />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Fade>
      </Stack>

      {/* Dialogs with better mobile support */}
      <Dialog
        open={createOpen}
        onClose={handleCloseCreate}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Typography variant="h6" fontWeight={600}>Add Category</Typography>
          {isMobile && (
            <IconButton size="small" onClick={handleCloseCreate}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              value={form.name}
              onChange={(e) => {
                const nextName = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  name: nextName,
                  slug: prev.slug.trim().length > 0 ? prev.slug : slugify(nextName),
                }));
              }}
              fullWidth
              autoFocus
              size={isMobile ? "medium" : "small"}
            />

            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  slug: slugify(e.target.value),
                }))
              }
              helperText="Leave as-is or customize the URL slug"
              fullWidth
              size={isMobile ? "medium" : "small"}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: 1,
        }}>
          <Button
            fullWidth={isMobile}
            onClick={handleCloseCreate}
            disabled={saving}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "Creating..." : "Create Category"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Typography variant="h6" fontWeight={600}>Edit Category</Typography>
          {isMobile && (
            <IconButton size="small" onClick={handleCloseEdit}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              fullWidth
              autoFocus
              size={isMobile ? "medium" : "small"}
            />
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  slug: slugify(e.target.value),
                }))
              }
              helperText="Updating the slug changes the public URL"
              fullWidth
              size={isMobile ? "medium" : "small"}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: 1,
        }}>
          <Button
            fullWidth={isMobile}
            onClick={handleCloseEdit}
            disabled={saving}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={handleEdit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Typography variant="h6" fontWeight={600}>Delete Category</Typography>
          {isMobile && (
            <IconButton size="small" onClick={handleCloseDelete}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{selected?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action is only allowed for categories with no assigned products.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: 1,
        }}>
          <Button
            fullWidth={isMobile}
            onClick={handleCloseDelete}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            fullWidth={isMobile}
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Category"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar("")}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      />
    </Box>
  );
}